import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    AlertTriangle,
    ArrowRight,
    ExternalLink,
    Loader2,
    PenLine,
    X,
} from "lucide-react";
import { tahkimMerkez, TAHKIM_ASAMALARI } from "../../lib/tahkimApi";
import {
    asamaLabel,
    bosOnerilenAlanlar,
    veriGirisiGruplari,
} from "../../lib/tahkimAsamaConfig";

function FieldInput({ field, value, onChange }) {
    const base =
        "w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500/50";

    if (field.type === "checkbox") {
        return (
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="rounded border-white/20"
                />
                {field.label}
            </label>
        );
    }

    if (field.type === "select") {
        return (
            <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
                <option value="">Seçin…</option>
                {(field.options || []).map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.label}
                    </option>
                ))}
            </select>
        );
    }

    return (
        <input
            type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
            step={field.step}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            className={base}
        />
    );
}

export default function TahkimAsamaGecisModal({ open, dosya, hedefAsama, onClose, onSuccess }) {
    const [hedef, setHedef] = useState(hedefAsama || "");
    const [form, setForm] = useState({});
    const [panelNotlar, setPanelNotlar] = useState("");
    const [notEk, setNotEk] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open || !dosya) return;
        setHedef(hedefAsama || dosya.tahkim_asamasi || "BASVURU");
        setNotEk("");
        setError("");
        setPanelNotlar(dosya.panel_notlar || "");
    }, [open, dosya, hedefAsama]);

    useEffect(() => {
        if (!open || !dosya || !hedef) return;
        const seed = {};
        veriGirisiGruplari(hedef).forEach((g) => {
            g.fields.forEach((a) => {
                if (a.type === "checkbox") {
                    seed[a.key] = !!dosya[a.key];
                } else if (dosya[a.key] != null && dosya[a.key] !== "") {
                    seed[a.key] = dosya[a.key];
                }
            });
        });
        setForm(seed);
    }, [open, dosya, hedef]);

    const gruplar = useMemo(() => veriGirisiGruplari(hedef), [hedef]);
    const bosAlanlar = useMemo(
        () => (hedef ? bosOnerilenAlanlar(hedef, form, dosya || {}) : []),
        [hedef, form, dosya],
    );

    if (!open || !dosya) return null;

    const mevcutLabel = asamaLabel(dosya.tahkim_asamasi);
    const hedefLabel = asamaLabel(hedef);
    const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const gonder = async (skipWarn = false) => {
        if (!hedef) {
            setError("Hedef aşama seçin");
            return;
        }
        if (!skipWarn && bosAlanlar.length > 0) {
            setError("Boş alanlar var — yine de geçmek için alttaki butonu kullanın");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const payload = {
                tahkim_asamasi: hedef,
                sync_dosya_durumu: true,
                ...form,
                panel_notlar: panelNotlar,
            };
            if (notEk.trim()) payload.panel_notlar_ek = notEk.trim();
            await tahkimMerkez.asamaGuncelle(dosya.id, payload);
            onSuccess?.();
            onClose?.();
        } catch (err) {
            setError(err?.response?.data?.detail || "Aşama güncellenemedi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div
                role="dialog"
                className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border border-violet-500/30 bg-[#121A2F] shadow-2xl"
            >
                <div className="sticky top-0 z-10 flex items-start justify-between gap-3 p-4 border-b border-white/10 bg-[#121A2F]/95 backdrop-blur">
                    <div>
                        <h3 className="text-base font-semibold text-white flex items-center gap-2">
                            Aşama geçişi &amp; veri girişi
                            <span className="text-[10px] font-normal px-2 py-0.5 rounded border border-[#D4AF37]/30 text-[#D4AF37]">
                                Tahkim Veri Girişi
                            </span>
                        </h3>
                        <p className="text-xs text-slate-400 mt-1 font-mono">{dosya.dosya_no || `#${dosya.id}`}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs">
                            <span className="px-2 py-0.5 rounded border border-white/10 text-slate-300">
                                {mevcutLabel}
                            </span>
                            <ArrowRight className="h-3 w-3 text-slate-500" />
                            <span className="px-2 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-violet-200">
                                {hedefLabel}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <Link
                            to={`/e/tahkim-dosyalari/${dosya.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"
                        >
                            <PenLine className="h-3 w-3" />
                            Tam form
                            <ExternalLink className="h-3 w-3" />
                        </Link>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/10"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                            Hedef aşama (tüm aşamalar sürüklenebilir)
                        </label>
                        <select
                            value={hedef}
                            onChange={(e) => {
                                setHedef(e.target.value);
                                setError("");
                            }}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        >
                            {TAHKIM_ASAMALARI.map((a) => (
                                <option key={a.value} value={a.value}>
                                    {a.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-3 py-2 flex items-start gap-2 text-xs text-amber-100/90">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-400 mt-0.5" />
                        <span>
                            Aşağıdaki alanlar <strong>Tahkim Veri Girişi</strong> ile aynıdır — isteğe
                            bağlıdır, boş bırakılabilir.
                        </span>
                    </div>

                    {gruplar.map((grup) => (
                        <div
                            key={grup.asama}
                            className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-3 space-y-3"
                        >
                            <div className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold border-b border-white/5 pb-2">
                                {grup.label}
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {grup.fields.map((field) => (
                                    <div
                                        key={field.key}
                                        className={field.type === "checkbox" ? "sm:col-span-2" : ""}
                                    >
                                        {field.type !== "checkbox" && (
                                            <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                                                {field.label}
                                                <span className="text-slate-600 normal-case ml-1">(isteğe bağlı)</span>
                                            </label>
                                        )}
                                        <FieldInput
                                            field={field}
                                            value={form[field.key]}
                                            onChange={(v) => setField(field.key, v)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div className="rounded-xl border border-white/10 bg-[#0B0F19]/60 p-3">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                            Tahkim notları (veri girişi)
                        </label>
                        <textarea
                            value={panelNotlar}
                            onChange={(e) => setPanelNotlar(e.target.value)}
                            rows={3}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white resize-none"
                        />
                    </div>

                    <div>
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">
                            Geçiş notu ekle (isteğe bağlı)
                        </label>
                        <textarea
                            value={notEk}
                            onChange={(e) => setNotEk(e.target.value)}
                            rows={2}
                            placeholder="Bu geçişle ilgili kısa not…"
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white resize-none"
                        />
                    </div>

                    {bosAlanlar.length > 0 && (
                        <p className="text-[11px] text-slate-500">
                            Doldurulmamış önerilen alanlar:{" "}
                            {bosAlanlar.map((a) => a.label).join(", ")}
                        </p>
                    )}

                    {error && (
                        <p className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                            {error}
                        </p>
                    )}
                </div>

                <div className="sticky bottom-0 flex flex-wrap gap-2 p-4 border-t border-white/10 bg-[#0B0F19]/95 backdrop-blur">
                    <button
                        type="button"
                        onClick={() => gonder(false)}
                        disabled={busy}
                        className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                        Kaydet ve geç
                    </button>
                    {bosAlanlar.length > 0 && (
                        <button
                            type="button"
                            onClick={() => gonder(true)}
                            disabled={busy}
                            className="px-4 py-2 rounded-lg border border-white/15 text-slate-300 text-sm hover:bg-white/5 disabled:opacity-50"
                        >
                            Boş bırak — yine de geç
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-slate-500 text-sm hover:text-white"
                    >
                        İptal
                    </button>
                </div>
            </div>
        </div>
    );
}
